import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const RealTimeActivityFeed = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch initial activities
  useEffect(() => {
    fetchRecentActivities();
  }, [filter]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!autoRefresh) return;

    // Subscribe to user_profiles changes (new users, profile updates)
    const userChannel = supabase?.channel('user_activity')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_profiles'
        },
        (payload) => {
          handleUserActivity(payload);
        }
      )?.subscribe();

    // Subscribe to bookings changes (new bookings, status updates)
    const bookingChannel = supabase?.channel('booking_activity')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bookings'
        },
        (payload) => {
          handleBookingActivity(payload);
        }
      )?.subscribe();

    // Subscribe to rental_violation_charges (disputes)
    const disputeChannel = supabase?.channel('dispute_activity')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'rental_violation_charges'
        },
        (payload) => {
          handleDisputeActivity(payload);
        }
      )?.subscribe();

    // Cleanup subscriptions
    return () => {
      supabase?.removeChannel(userChannel);
      supabase?.removeChannel(bookingChannel);
      supabase?.removeChannel(disputeChannel);
    };
  }, [autoRefresh]);

  const fetchRecentActivities = async () => {
    setLoading(true);
    try {
      const activities = [];

      // Fetch recent user activities
      if (filter === 'all' || filter === 'users') {
        const { data: users, error: userError } = await supabase?.from('user_profiles')?.select('id, full_name, email, role, created_at, updated_at')?.order('created_at', { ascending: false })?.limit(20);

        if (!userError && users) {
          users?.forEach(user => {
            activities?.push({
              id: `user-${user?.id}`,
              type: 'user',
              action: 'joined',
              user: user?.full_name || user?.email,
              details: `New ${user?.role} account created`,
              timestamp: user?.created_at,
              icon: 'UserPlus',
              color: 'text-success'
            });
          });
        }
      }

      // Fetch recent bookings
      if (filter === 'all' || filter === 'bookings') {
        const { data: bookings, error: bookingError } = await supabase?.from('bookings')?.select(`
            id,
            booking_reference,
            status,
            total_amount,
            created_at,
            renter:user_profiles!bookings_renter_id_fkey(full_name, email)
          `)?.order('created_at', { ascending: false })?.limit(20);

        if (!bookingError && bookings) {
          bookings?.forEach(booking => {
            activities?.push({
              id: `booking-${booking?.id}`,
              type: 'booking',
              action: booking?.status,
              user: booking?.renter?.full_name || booking?.renter?.email || 'Unknown',
              details: `Booking ${booking?.booking_reference} - $${booking?.total_amount}`,
              timestamp: booking?.created_at,
              icon: 'Calendar',
              color: getBookingStatusColor(booking?.status)
            });
          });
        }
      }

      // Fetch recent disputes
      if (filter === 'all' || filter === 'disputes') {
        const { data: disputes, error: disputeError } = await supabase?.from('rental_violation_charges')?.select(`
            id,
            charge_reference,
            charge_status,
            charge_amount,
            violation_reason,
            created_at,
            renter:user_profiles!rental_violation_charges_renter_id_fkey(full_name, email)
          `)?.order('created_at', { ascending: false })?.limit(20);

        if (!disputeError && disputes) {
          disputes?.forEach(dispute => {
            activities?.push({
              id: `dispute-${dispute?.id}`,
              type: 'dispute',
              action: dispute?.charge_status,
              user: dispute?.renter?.full_name || dispute?.renter?.email || 'Unknown',
              details: `${dispute?.violation_reason} - $${dispute?.charge_amount}`,
              timestamp: dispute?.created_at,
              icon: 'AlertTriangle',
              color: getDisputeStatusColor(dispute?.charge_status)
            });
          });
        }
      }

      // Sort all activities by timestamp
      activities?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setActivities(activities?.slice(0, 50));
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserActivity = (payload) => {
    const { eventType, new: newRecord, old: oldRecord } = payload;
    
    let activity = {
      id: `user-${newRecord?.id || oldRecord?.id}-${Date.now()}`,
      type: 'user',
      timestamp: new Date()?.toISOString(),
      icon: 'UserPlus',
      color: 'text-success'
    };

    if (eventType === 'INSERT') {
      activity.action = 'joined';
      activity.user = newRecord?.full_name || newRecord?.email;
      activity.details = `New ${newRecord?.role} account created`;
    } else if (eventType === 'UPDATE') {
      activity.action = 'updated';
      activity.user = newRecord?.full_name || newRecord?.email;
      activity.details = 'Profile updated';
      activity.icon = 'User';
      activity.color = 'text-primary';
    }

    if (filter === 'all' || filter === 'users') {
      setActivities(prev => [activity, ...prev]?.slice(0, 50));
    }
  };

  const handleBookingActivity = (payload) => {
    const { eventType, new: newRecord, old: oldRecord } = payload;
    
    let activity = {
      id: `booking-${newRecord?.id || oldRecord?.id}-${Date.now()}`,
      type: 'booking',
      timestamp: new Date()?.toISOString(),
      icon: 'Calendar'
    };

    if (eventType === 'INSERT') {
      activity.action = 'created';
      activity.user = 'New booking';
      activity.details = `Booking ${newRecord?.booking_reference} - $${newRecord?.total_amount}`;
      activity.color = 'text-success';
    } else if (eventType === 'UPDATE') {
      activity.action = newRecord?.status;
      activity.user = 'Booking updated';
      activity.details = `${newRecord?.booking_reference} status: ${newRecord?.status}`;
      activity.color = getBookingStatusColor(newRecord?.status);
    }

    if (filter === 'all' || filter === 'bookings') {
      setActivities(prev => [activity, ...prev]?.slice(0, 50));
    }
  };

  const handleDisputeActivity = (payload) => {
    const { eventType, new: newRecord, old: oldRecord } = payload;
    
    let activity = {
      id: `dispute-${newRecord?.id || oldRecord?.id}-${Date.now()}`,
      type: 'dispute',
      timestamp: new Date()?.toISOString(),
      icon: 'AlertTriangle'
    };

    if (eventType === 'INSERT') {
      activity.action = 'filed';
      activity.user = 'New dispute';
      activity.details = `${newRecord?.violation_reason} - $${newRecord?.charge_amount}`;
      activity.color = 'text-error';
    } else if (eventType === 'UPDATE') {
      activity.action = newRecord?.charge_status;
      activity.user = 'Dispute updated';
      activity.details = `${newRecord?.charge_reference} status: ${newRecord?.charge_status}`;
      activity.color = getDisputeStatusColor(newRecord?.charge_status);
    }

    if (filter === 'all' || filter === 'disputes') {
      setActivities(prev => [activity, ...prev]?.slice(0, 50));
    }
  };

  const getBookingStatusColor = (status) => {
    const colors = {
      pending: 'text-warning',
      confirmed: 'text-success',
      completed: 'text-primary',
      cancelled: 'text-error'
    };
    return colors?.[status] || 'text-muted-foreground';
  };

  const getDisputeStatusColor = (status) => {
    const colors = {
      pending: 'text-warning',
      paid: 'text-success',
      disputed: 'text-error',
      refunded: 'text-primary',
      cancelled: 'text-muted-foreground'
    };
    return colors?.[status] || 'text-muted-foreground';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Activity" size={20} strokeWidth={2} className="text-primary" />
          </div>
          <div>
            <h3 className="font-heading text-xl font-bold text-foreground">Live Activity Feed</h3>
            <p className="font-body text-sm text-muted-foreground">Real-time platform events</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* Auto-refresh toggle */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md font-body text-sm transition-smooth ${
              autoRefresh
                ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
            }`}
          >
            <Icon name={autoRefresh ? 'Wifi' : 'WifiOff'} size={16} strokeWidth={2} />
            <span>{autoRefresh ? 'Live' : 'Paused'}</span>
          </button>
          <button
            onClick={fetchRecentActivities}
            className="p-2 rounded-md bg-muted hover:bg-muted/80 transition-smooth"
          >
            <Icon name="RefreshCw" size={16} strokeWidth={2} className="text-foreground" />
          </button>
        </div>
      </div>
      {/* Filter tabs */}
      <div className="flex space-x-2 mb-4">
        {['all', 'users', 'bookings', 'disputes']?.map((filterType) => (
          <button
            key={filterType}
            onClick={() => setFilter(filterType)}
            className={`px-4 py-2 rounded-md font-body text-sm font-medium transition-smooth capitalize ${
              filter === filterType
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            {filterType}
          </button>
        ))}
      </div>
      {/* Activity list */}
      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 border-3 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="font-body text-muted-foreground">Loading activities...</span>
            </div>
          </div>
        ) : activities?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="font-body text-muted-foreground">No recent activities</p>
          </div>
        ) : (
          activities?.map((activity) => (
            <div
              key={activity?.id}
              className="flex items-start space-x-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-smooth"
            >
              <div className={`w-8 h-8 rounded-full bg-card flex items-center justify-center flex-shrink-0 ${activity?.color}`}>
                <Icon name={activity?.icon} size={16} strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="font-body font-medium text-foreground">{activity?.user}</span>
                  <span className="font-body text-xs text-muted-foreground">{activity?.action}</span>
                </div>
                <p className="font-body text-sm text-muted-foreground truncate">{activity?.details}</p>
              </div>
              <span className="font-body text-xs text-muted-foreground whitespace-nowrap">
                {formatTimestamp(activity?.timestamp)}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RealTimeActivityFeed;